import { cn } from '@/lib/utils';
import { Label } from '@/shared/components/ui/label';

import type { ModalMapTooltipItem, ModalMapTooltipProps } from './ModalMapTooltip.types';

const formatCoords = (coords?: ModalMapTooltipProps['coords']) => {
  if (!coords || coords.lat == null || coords.lng == null) {
    return '-';
  }

  return `${coords.lat}, ${coords.lng}`;
};

const formatValue = (value?: string | null) => value ?? '-';

const timestampFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jakarta',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const parts = timestampFormatter.formatToParts(date);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value ?? '';

  return `${getPart('day')} ${getPart('month')} ${getPart('year')} · ${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
};

const ModalMapTooltip = ({ title, item, coords, onItemClick }: ModalMapTooltipProps) => {
  const items = item ?? [];
  const hasItems = items.length > 0;

  return (
    <div
      className={cn(
        'w-[320px] max-w-[calc(100vw-24px)] rounded-[24px] border border-[#d4dde7] bg-white shadow-[0_18px_40px_rgba(15,_23,_42,_0.16)]',
        'dark:border-[#2B465F] dark:bg-db-wrap'
      )}
    >
      <div className={cn('flex flex-col gap-3 px-3 py-3')}>
        <div
          className={cn(
            'flex items-start justify-between gap-3 border-b border-[#e5e7eb] pb-3',
            'dark:border-[#2B465F]'
          )}
        >
          <div className={cn('flex min-w-0 flex-col gap-1')}>
            {/* A cluster of markers with no shared name/coords (e.g. PSCDashboard's
                stacked-marker popup) omits these instead of showing a bare "-" -
                a marker with a real title (e.g. Traffic Dashboard's CCTV name)
                still gets its usual header. */}
            {title != null && (
              <Label
                className={cn(
                  'truncate text-[16px] font-medium leading-[20px]',
                  'dark:text-text-white'
                )}
              >
                {formatValue(title)}
              </Label>
            )}
            {coords && (coords.lat != null || coords.lng != null) && (
              <p className={cn('body2 dark:text-[#cbd5e1]')}>{formatCoords(coords)}</p>
            )}
          </div>

          <div className={cn('shrink-0 pt-1 body2', 'dark:text-[#cbd5e1]')}>
            {items.length} Items
          </div>
        </div>

        <div className={cn('max-h-[190px] overflow-y-auto pr-1')}>
          <div className={cn('flex flex-col gap-3')}>
            {hasItems ? (
              items.map((entry, index) => {
                const clickable = entry.id != null && !!onItemClick;
                const key = `${entry.title ?? '-'}-${entry.timestamp ?? '-'}-${index}`;
                const rowClassName = cn(
                  'flex items-start gap-3 rounded-[18px] w-full text-start',
                  clickable && 'cursor-pointer hover:bg-[#f3f6fe] dark:hover:bg-[#12324a]/40'
                );
                const rowContent = (
                  <>
                    <div
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                        'bg-[#dceeff] text-[14px] font-medium text-[#1d73d8]',
                        'dark:bg-[#12324a] dark:text-[#8ec5ff]'
                      )}
                    >
                      {index + 1}
                    </div>

                    <div className={cn('min-w-0 flex-1')}>
                      <p className={cn('truncate body1medium dark:text-text-white')}>
                        {formatValue(entry.title)}
                      </p>
                      {/* timestamp wins when both are set; subtitle (e.g. an
                          ambulance's home facility) is the fallback second
                          line for rows with no time of their own. Rows with
                          neither skip this line rather than showing a bare
                          "-" under every entry. */}
                      {entry.timestamp != null ? (
                        <p className={cn('mt-1 body2 dark:text-[#cbd5e1]')}>
                          {formatTimestamp(entry.timestamp)}
                        </p>
                      ) : (
                        entry.subtitle != null && (
                          <p className={cn('mt-1 body2 dark:text-[#cbd5e1]')}>
                            {formatValue(entry.subtitle)}
                          </p>
                        )
                      )}
                    </div>
                  </>
                );

                if (clickable) {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onItemClick(entry)}
                      className={rowClassName}
                    >
                      {rowContent}
                    </button>
                  );
                }

                return (
                  <div key={key} className={rowClassName}>
                    {rowContent}
                  </div>
                );
              })
            ) : (
              <div className={cn('flex items-start gap-3 rounded-[18px]')}>
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    'bg-[#dceeff] text-[14px] font-medium text-[#1d73d8]',
                    'dark:bg-[#12324a] dark:text-[#8ec5ff]'
                  )}
                >
                  -
                </div>

                <div className={cn('min-w-0 flex-1')}>
                  <p className={cn('truncate body1medium dark:text-text-white')}>-</p>
                  <p className={cn('mt-1 body2 dark:text-[#cbd5e1]')}>-</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMapTooltip;
